// The console's session probe (spec 15 §3.1.6): "the SPA admin area mirrors
// this — router guard requires /v1/platform/me 200 before rendering anything;
// 401 → login screen".
//
// One query key for the whole area so the guard, the landing screen and the
// sign-out button all read the same answer and a sign-out invalidates it once.
// `retry: false` on purpose: a 401 is an ANSWER, not a flake, and retrying it
// three times just delays the redirect to /login by a second.
//
// THE RULE THIS MODULE EXISTS TO ENFORCE (the class behind the 2026-08-10
// blocker): **every call that changes the SERVER's session phase must update
// this cached projection of it in the same breath.** The cache is not a
// convenience here — it is what the router guard branches on, so a phase the
// server has already moved and the cache has not is a guard deciding on a lie.
// It cost exactly that: `POST auth/totp/confirm` flipped a staff member to
// `active` and nothing touched the cache, so `staleTime`'s 30 seconds left
// `/console`'s guard reading `totp_confirmed: false`, bouncing to `/enroll`,
// re-firing an enrolment the API then refused 409 — on the very click the
// recovery-codes screen tells the user to make, taking the ten codes off the
// glass with it. Hence the named mutators below: no screen writes this key by
// hand, and every phase transition has one.

import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { fetchPlatformMe, type PlatformMe } from "../lib/platform-api";

export const PLATFORM_ME_QUERY_KEY = ["platform", "me"] as const;

export function usePlatformSession(): UseQueryResult<PlatformMe, unknown> {
  return useQuery({
    queryKey: PLATFORM_ME_QUERY_KEY,
    retry: false,
    // The window-focus refetch is off DELIBERATELY: the enrolment screen shows
    // the recovery codes exactly once, and a background refetch that flips
    // `totp_confirmed` while they are on the glass must never be able to move
    // the user off them (the phase machine also guards this — belt and braces).
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    queryFn: ({ signal }) => fetchPlatformMe(signal),
  });
}

/** Drop the cached session — used by sign-out so nothing renders stale identity. */
export function useForgetPlatformSession(): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.removeQueries({ queryKey: PLATFORM_ME_QUERY_KEY });
  };
}

/**
 * The `totp_pending → active` transition, mirrored into the cache the instant
 * `POST auth/totp/confirm` returns 200.
 *
 * WHY WRITE THE VALUE RATHER THAN INVALIDATE. An invalidation is a REQUEST for
 * the truth; this is the truth — the 200 that just landed IS the server saying
 * the phase moved, so there is nothing to go and ask. Invalidating instead
 * would leave the guard reading `false` until a round trip completed, which is
 * the same race with a shorter fuse. The write is guarded on an existing entry:
 * with no session cached there is no guard decision to poison, and manufacturing
 * a `PlatformMe` here would mean inventing an id, an email and a role.
 */
export function useMarkTotpConfirmed(): () => void {
  const queryClient = useQueryClient();
  return () => {
    queryClient.setQueryData<PlatformMe>(PLATFORM_ME_QUERY_KEY, (previous) =>
      previous === undefined ? undefined : { ...previous, totp_confirmed: true },
    );
  };
}

/**
 * A reissued batch of recovery codes, mirrored into the cache (spec 15 §3.1
 * step 4, D-570 ruling 1): the low-codes notice reads the count from here, so
 * it goes away the moment the server answers with the new batch, by the
 * module's rule above. Guarded on an existing entry like the phase writer.
 */
export function useMarkRecoveryCodesReissued(): (count: number) => void {
  const queryClient = useQueryClient();
  return (count: number) => {
    queryClient.setQueryData<PlatformMe>(PLATFORM_ME_QUERY_KEY, (current) =>
      current === undefined ? current : { ...current, recovery_codes_remaining: count },
    );
  };
}
